import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  ListItemButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetLojaAtualQuery, useGetMeusPedidosQuery, useGetPedidoStatusQuery, type MeuPedido } from '../../api';
import { pedidoNaoEncontrado, removerPedidoLocal, usePedidosLocais, VALIDADE_PEDIDO_LOCAL_MS } from '../../lib/pedidos-locais';
import type { RootState } from '../../store';
import { formatarBRL } from '../../lib/formato';
import { CabecalhoModal } from '../../components/cabecalho-modal';
import { DialogLogin } from '../../components/dialog-login';
import { EstadoErro, EstadoVazio } from '../../components/estado-mensagem';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const LABEL_SITUACAO: Record<string, string> = {
  PENDENTE: 'Pendente',
  AGUARDANDO_PAGAMENTO: 'Aguardando pagamento',
  ENVIADO: 'Enviado',
  CONFIRMADO: 'Confirmado',
  AGUARDANDO_RETIRADA: 'Aguardando retirada',
  ENTREGUE: 'Entregue',
  CANCELADO: 'Cancelado',
  FALHA_ENVIO: 'Falha no envio',
};

function corSituacao(s: string): 'default' | 'success' | 'error' | 'info' | 'warning' {
  if (s === 'ENTREGUE') return 'success';
  if (s === 'CANCELADO' || s === 'FALHA_ENVIO') return 'error';
  if (s === 'CONFIRMADO' || s === 'AGUARDANDO_RETIRADA') return 'info';
  if (s === 'AGUARDANDO_PAGAMENTO') return 'warning';
  return 'default';
}

function iconeOrigem(origem: MeuPedido['origem']): JSX.Element {
  if (origem === 'DELIVERY') return <DeliveryDiningIcon fontSize="small" />;
  if (origem === 'TOTEM') return <StorefrontIcon fontSize="small" />;
  return <TableRestaurantIcon fontSize="small" />;
}

const fmtData = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export function MeusPedidosPage(): JSX.Element {
  const navigate = useNavigate();
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const theme = useTheme();
  // Desktop: meus pedidos abrem como modal centralizado; mobile: tela cheia.
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Paginação acumulada ("carregar mais"): a página é controlada aqui e o
  // banco sempre pagina (LIMIT/OFFSET); os itens são concatenados na ordem.
  const [pagina, setPagina] = useState(1);
  const [pedidos, setPedidos] = useState<MeuPedido[]>([]);
  const [temMais, setTemMais] = useState(false);
  const { data, isLoading, isFetching, isError, refetch } = useGetMeusPedidosQuery(pagina, { skip: !accessToken });
  const { data: loja, isLoading: carregandoLoja } = useGetLojaAtualQuery();
  const idConfiguracao = loja?.configuracao.id ?? null;
  const pedidosLocais = usePedidosLocais(idConfiguracao);
  const [loginAberto, setLoginAberto] = useState(false);

  useEffect(() => {
    if (!data) return;
    setTemMais(data.temMais);
    setPedidos((prev) => {
      if (data.pagina === 1) return data.itens;
      const jaTem = new Set(prev.map((p) => p.id));
      return [...prev, ...data.itens.filter((p) => !jaTem.has(p.id))];
    });
  }, [data]);

  const fechar = (): void => {
    navigate('/catalogo');
  };

  // Sem login e sem pedido feito neste aparelho: abre o login no lugar (sem navegar
  // pra outra rota). Ao concluir, o token entra na store e a query carrega os pedidos;
  // fechar sem logar volta ao cardápio.
  // Sem a loja ainda não dá para saber se há pedido neste aparelho: espera, em vez de piscar o login.
  if (!accessToken && carregandoLoja) return <></>;
  if (!accessToken && pedidosLocais.length === 0) {
    return <DialogLogin aberto onFechar={fechar} onSucesso={() => undefined} />;
  }

  // Sem login, com pedido feito neste aparelho: lista os locais e oferece o login para o resto.
  if (!accessToken && idConfiguracao != null) {
    const dias = Math.round(VALIDADE_PEDIDO_LOCAL_MS / 86_400_000);
    return (
      <Dialog
        open
        onClose={fechar}
        fullScreen={!ehDesktop}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{ sx: { position: 'relative', ...(ehDesktop ? { height: '90vh' } : {}) } }}
      >
        <CabecalhoModal titulo="Meus pedidos" aoFechar={fechar} />
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <Alert
            severity="info"
            icon={false}
            sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
            action={
              <Button color="inherit" size="small" onClick={() => setLoginAberto(true)} sx={{ fontWeight: 600 }}>
                Entrar
              </Button>
            }
          >
            Pedidos feitos neste aparelho nos últimos {dias} dias. Entre na sua conta para ver todos.
          </Alert>
          <Stack spacing={1.5}>
            {pedidosLocais.map((p) => (
              <CartaoPedidoLocal
                key={p.id}
                id={p.id}
                idConfiguracao={idConfiguracao}
                onClick={() => navigate(`/pedido/${p.id}`)}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogLogin aberto={loginAberto} onFechar={() => setLoginAberto(false)} onSucesso={() => setLoginAberto(false)} />
      </Dialog>
    );
  }

  const carregandoInicial = (isLoading || isFetching) && pagina === 1 && pedidos.length === 0;
  const vazio = !carregandoInicial && !isError && pedidos.length === 0;

  return (
    <Dialog
      open
      onClose={fechar}
      fullScreen={!ehDesktop}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{ sx: { position: 'relative', ...(ehDesktop ? { height: '90vh' } : {}) } }}
    >
      <CabecalhoModal titulo="Meus pedidos" aoFechar={fechar} />

      <DialogContent sx={{ pt: 2, pb: 2 }}>

        {carregandoInicial && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <EstadoErro titulo="Não foi possível carregar seus pedidos" aoTentarNovamente={() => void refetch()} />
        )}
        {vazio && (
          <EstadoVazio
            icone={<ReceiptLongIcon />}
            titulo="Você ainda não fez pedidos nesta loja"
            descricao="Quando fizer, eles aparecem aqui com o status de cada um."
          />
        )}

        <Stack spacing={1.5}>
          {pedidos.map((p) => (
            <CartaoPedido key={p.id} pedido={p} onClick={() => navigate(`/pedido/${p.id}`)} />
          ))}
        </Stack>

        {temMais && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button onClick={() => setPagina((p) => p + 1)} disabled={isFetching}>
              {isFetching ? <CircularProgress size={20} /> : 'Carregar mais'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Pedido feito sem login neste aparelho: o aparelho só guarda a referência, então o
 * cartão busca o status na API pública do pedido. Pedido que a API não conhece mais sai da lista.
 */
function CartaoPedidoLocal({
  id,
  idConfiguracao,
  onClick,
}: {
  id: string;
  idConfiguracao: number;
  onClick: () => void;
}): JSX.Element | null {
  const { data, error, refetch } = useGetPedidoStatusQuery(id, { refetchOnMountOrArgChange: true });
  const naoEncontrado = pedidoNaoEncontrado(error);

  useEffect(() => {
    if (naoEncontrado) removerPedidoLocal(idConfiguracao, id);
  }, [naoEncontrado, idConfiguracao, id]);

  if (naoEncontrado) return null;
  if (error && !data) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Não foi possível carregar este pedido.
          </Typography>
          <Button size="small" onClick={() => void refetch()}>
            Tentar de novo
          </Button>
        </Stack>
      </Paper>
    );
  }
  if (!data) return <Skeleton variant="rounded" height={76} sx={{ borderRadius: 2 }} />;

  const pedido: MeuPedido = {
    id: data.id,
    shortId: data.shortId,
    origem: data.origem,
    situacao: data.situacao,
    statusPagamento: null,
    dataCriacao: data.dataCriacao,
    dataUltimaAtualizacao: data.dataUltimaAtualizacao,
    total: data.payloadJson?.valorTotal ?? null,
    // Mesma conta do `/me/pedidos` (linhas do pedido), para os dois cartões dizerem o mesmo.
    qtdItens: data.payloadJson?.itens?.length ?? 0,
  };
  return <CartaoPedido pedido={pedido} onClick={onClick} />;
}

/** Cartão de um pedido na listagem (origem, código, status, data, valor). */
function CartaoPedido({ pedido, onClick }: { pedido: MeuPedido; onClick: () => void }): JSX.Element {
  const codigo = pedido.shortId ?? pedido.id.slice(0, 8).toUpperCase();
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <ListItemButton onClick={onClick} sx={{ p: 2, gap: 1.5, alignItems: 'flex-start' }}>
        <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 40, height: 40 }}>
          {iconeOrigem(pedido.origem)}
        </Avatar>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              #{codigo}
            </Typography>
            <Chip
              size="small"
              label={LABEL_SITUACAO[pedido.situacao] ?? pedido.situacao}
              color={corSituacao(pedido.situacao)}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {fmtData.format(new Date(pedido.dataCriacao))}
            {' · '}
            {pedido.qtdItens} {pedido.qtdItens === 1 ? 'item' : 'itens'}
          </Typography>
        </Box>

        {pedido.total != null && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flexShrink: 0, alignSelf: 'center' }} color="primary">
            {formatarBRL(pedido.total)}
          </Typography>
        )}
      </ListItemButton>
    </Paper>
  );
}
